// The library's one search field — shown on all four tabs.
//
// A `role="combobox"` over a listbox of suggestions rather than a plain input,
// because the dropdown is what makes the field worth having on Reading Now /
// Book Store / Collections at all: from a discovery tab a reader wants the BOOK,
// not a trip to a results page. Enter with nothing highlighted still runs a full
// search, so it degrades to an ordinary search box for anyone who ignores it.
//
// PRESENTATIONAL. It never fetches: the parent owns the debounce and the request
// and hands the matches back down. That is what keeps one field usable from a
// page that already knows how to talk to the shelf, and testable without a
// network. `onDraft` fires on every keystroke — debouncing is the caller's job.

import { useEffect, useId, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import type { ShelfBook } from "../../lib/library-types";
import { BookCover } from "./BookCover";
import { QUIET_LINK } from "./recipes";
import { plural } from "../../lib/numberWords";

interface ShelfSearchFieldProps {
  /** The COMMITTED query — what the results below are actually showing. */
  value: string;
  /** Matches for the current draft. The caller caps the length; 6 is the design. */
  suggestions: ShelfBook[];
  /** Whole match count for the draft, for the "See all" footer. */
  suggestionTotal: number;
  /** True while a suggestion request is in flight, so an empty list can be
   *  distinguished from "no matches" and not flash a false dead end. */
  suggesting: boolean;
  /** Every keystroke. The caller debounces and fetches. */
  onDraft: (draft: string) => void;
  /** Enter, the magnifier, or "See all results". */
  onSubmit: (query: string) => void;
  /** Picking a suggestion opens that book instead of running a search. */
  onOpenBook: (book: ShelfBook) => void;
}

export function ShelfSearchField({
  value,
  suggestions,
  suggestionTotal,
  suggesting,
  onDraft,
  onSubmit,
  onOpenBook,
}: ShelfSearchFieldProps) {
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const wrap = useRef<HTMLDivElement>(null);

  // The committed query wins whenever it changes underneath us — the reader
  // cleared the search from the results header, or landed on a `?q=` URL.
  useEffect(() => setDraft(value), [value]);

  // Click-away closes the dropdown. `pointerdown`, not `click`, so it lands
  // before the input's blur reorders things.
  useEffect(() => {
    if (!open) return;
    function onAway(event: PointerEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onAway);
    return () => document.removeEventListener("pointerdown", onAway);
  }, [open]);

  const trimmed = draft.trim();
  const expanded = open && trimmed.length > 0;

  function commit(query: string) {
    setOpen(false);
    setActive(-1);
    onSubmit(query.trim());
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }

    // Enter is handled HERE rather than left to the form's implicit submission.
    // Implicit submission is conditional — it needs a submit button or exactly
    // one listed element, and this form also carries the clear button — so
    // relying on it makes Enter a silent no-op. Explicit is the whole contract.
    if (event.key === "Enter") {
      event.preventDefault();
      if (expanded && active >= 0 && suggestions[active]) {
        setOpen(false);
        onOpenBook(suggestions[active]);
      } else {
        commit(draft);
      }
      return;
    }

    if (!expanded || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  }

  return (
    // Width is the PARENT's business — full width under the tabs on a phone, a
    // narrow control beside them on a desktop.
    <div ref={wrap} className="relative w-full">
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          commit(draft);
        }}
        className={[
          "flex items-center gap-2 rounded-input border-[1.5px] border-hairline-bold bg-field",
          "px-3 py-1.5 shadow-card transition-shadow duration-200 ease-observatory",
          "focus-within:shadow-card-hi",
        ].join(" ")}
      >
        {/* A REAL submit button, not a decorative icon — see the Enter note above. */}
        <button
          type="submit"
          className="shrink-0 rounded-full p-0.5 text-ink-faint transition-colors duration-200 ease-observatory hover:text-terracotta"
        >
          <Search className="h-[15px] w-[15px]" aria-hidden="true" />
          <span className="sr-only">Search</span>
        </button>
        <input
          type="search"
          value={draft}
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          aria-label="Search the library by title or author"
          // Short enough for a narrow field; the accessible name above stays the
          // long, explicit one.
          placeholder="Title or author"
          autoComplete="off"
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
            setActive(-1);
            onDraft(event.target.value);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={[
            "min-w-0 flex-1 bg-transparent font-body text-[15px] text-ink-body",
            "placeholder:text-ink-faint focus:outline-none",
            // Safari paints its own clear affordance; ours is the X button.
            "[&::-webkit-search-cancel-button]:appearance-none",
          ].join(" ")}
        />
        {draft && (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              setActive(-1);
              onDraft("");
              commit("");
            }}
            className="shrink-0 rounded-full p-0.5 text-ink-faint transition-colors duration-200 ease-observatory hover:text-terracotta"
          >
            <X className="h-[15px] w-[15px]" aria-hidden="true" />
            <span className="sr-only">Clear the search</span>
          </button>
        )}
      </form>

      {expanded && (
        <div
          className={[
            "absolute top-[calc(100%+6px)] z-30 overflow-hidden",
            // Anchored to the field's RIGHT edge and allowed to be wider than it.
            // The field is deliberately narrow; book titles are not, and a
            // 236px dropdown would truncate almost every one of them.
            "left-0 sm:left-auto sm:right-0 sm:w-[380px]",
            "rounded-input border-[1.5px] border-hairline-bold bg-card shadow-card",
          ].join(" ")}
        >
          {suggestions.length === 0 ? (
            <p className="px-4 py-4 font-body text-[15px] text-ink-faint">
              {/* "Searching…" and "nothing found" are different facts. Showing the
                  dead end while the request is still out would be a lie that
                  corrects itself, which reads as a flicker. */}
              {suggesting ? "Searching…" : `Nothing on the shelf matches “${trimmed}”.`}
            </p>
          ) : (
            <>
              <ul id={listId} role="listbox" aria-label="Search suggestions" className="list-none">
                {suggestions.map((book, index) => (
                  <li
                    key={book.id}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={index === active}
                  >
                    <button
                      type="button"
                      // Mousedown, not click: the input's blur would otherwise
                      // close the dropdown before the click lands.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setOpen(false);
                        onOpenBook(book);
                      }}
                      onMouseEnter={() => setActive(index)}
                      className={[
                        "flex w-full items-center gap-3 px-3 py-2.5 text-left",
                        "transition-colors duration-200 ease-observatory",
                        index === active ? "bg-terracotta-wash" : "bg-transparent",
                      ].join(" ")}
                    >
                      <span className="w-9 shrink-0">
                        <BookCover book={book} size="row" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-serif text-[17px] leading-tight text-ink-body">
                          {book.title}
                        </span>
                        <span className="block truncate font-body text-[14px] leading-snug text-ink-faint">
                          {/* Say WHY an author-only hit is in the list, or the row
                              looks like a mistake to someone who typed a name. */}
                          {matchedOnAuthor(book, trimmed) ? "by " : ""}
                          {book.author_name ?? "Unattributed"}
                          {book.category_label ? ` · ${book.category_label.toLowerCase()}` : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(trimmed);
                }}
                className="flex w-full items-center justify-between border-t border-hairline px-4 py-3 text-left transition-colors duration-200 ease-observatory hover:bg-paper-deep"
              >
                <span className={QUIET_LINK}>See all results</span>
                <span className="font-mono text-[10px] uppercase tracking-smallcaps tabular-nums text-ink-faint">
                  {plural(suggestionTotal, "match", "matches")}
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Did this row match on the BYLINE rather than the title?
 *
 * Presentation only — the server decides what matches; this just explains a row
 * whose title contains nothing the reader typed. It mirrors the server's rule
 * (`repositories/library._byline_expr`) closely enough for that purpose: both
 * look at title and byline, case-insensitively, as substrings.
 */
export function matchedOnAuthor(book: ShelfBook, query: string): boolean {
  const q = normalise(query);
  if (!q) return false;
  return !normalise(book.title).includes(q) && normalise(book.author_name ?? "").includes(q);
}

function normalise(value: string): string {
  return value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
